'use client';

import { forwardRef, useState, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { withDisplayName } from '../../lib/display-name';
import { SIcon } from '../SIcon';

export interface SListItemRenderState {
  hovered: boolean;
  disabled: boolean;
}

export type SListItemRenderProp = (state: SListItemRenderState) => ReactNode;
export type SListItemSlot = ReactNode | SListItemRenderProp;
export type SListItemInteraction = 'chevron';
export type SListItemSupportingTextPosition = 'right' | 'bottom';
export type SListItemSize = 'sm' | 'md';

export interface SListItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  /** 리스트 아이템 제목 */
  title: SListItemSlot;
  /** 제목을 보조하는 텍스트 */
  supportingText?: SListItemSlot;
  /** 보조 텍스트 위치 */
  supportingTextPosition?: SListItemSupportingTextPosition;
  /** 중첩 단계. Figma 기준 1~3단계 */
  depth?: 1 | 2 | 3;
  /** 타이틀 앞에 표시할 아이콘/콘텐츠 */
  leading?: SListItemSlot;
  /** 타이틀 뒤에 표시할 태그/콘텐츠 */
  trailing?: SListItemSlot;
  /** 외곽 테두리 사용 여부 */
  bordered?: boolean;
  /** 선택 상태 여부 */
  selected?: boolean;
  /** 클릭 가능 상태 여부 */
  clickable?: boolean;
  /** hover/selected 상태에서 적용할 인터랙션 preset */
  interaction?: SListItemInteraction;
  /** 아이템 왼쪽 accent stripe 표시 여부 */
  accentStripe?: boolean;
  /** 조밀한 높이 사용 여부 */
  dense?: boolean;
  /** 타이포그래피 크기 */
  size?: SListItemSize;
  /** 비활성 상태 여부 */
  disabled?: boolean;
}

const SListItemImpl = forwardRef<HTMLDivElement, SListItemProps>(function SListItem(
  {
    title,
    supportingText,
    supportingTextPosition = 'right',
    depth = 1,
    leading,
    trailing,
    bordered = false,
    selected = false,
    clickable = true,
    interaction,
    accentStripe = false,
    dense = false,
    size = 'sm',
    disabled = false,
    className,
    onMouseEnter,
    onMouseLeave,
    ...rest
  },
  ref,
) {
  const [hovered, setHovered] = useState(false);
  const renderState = { hovered, disabled };
  const titleNode = typeof title === 'function' ? title(renderState) : title;
  const supportingTextNode =
    typeof supportingText === 'function' ? supportingText(renderState) : supportingText;
  const hasSupportingText = supportingTextNode != null && supportingTextNode !== '';
  const leadingNode = typeof leading === 'function' ? leading(renderState) : leading;
  const trailingNode = typeof trailing === 'function' ? trailing(renderState) : trailing;
  const isInteractive = clickable && !disabled;
  const isSelected = isInteractive && selected;
  const hasChevronInteraction = interaction === 'chevron';
  const showInteractionChevron = hasChevronInteraction && isInteractive && (isSelected || hovered);
  const interactionChevronColor = isSelected
    ? 'var(--sys-color-fg-brand)'
    : 'var(--sys-color-fg-quaternary)';
  const titleTypo =
    size === 'sm'
      ? isSelected
        ? 'typo-item-sm-selected'
        : 'typo-item-sm-default'
      : isSelected
        ? 'typo-item-md-selected'
        : 'typo-item-md-default';

  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    if (isInteractive) setHovered(true);
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    setHovered(false);
    onMouseLeave?.(event);
  };

  return (
    <div
      ref={ref}
      aria-disabled={disabled || undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'group/list-item relative flex w-full items-center justify-between bg-(--cmp-listItem-bg-default) text-left text-(--cmp-listItem-text-default)',
        titleTypo,
        disabled
          ? 'pointer-events-none cursor-not-allowed text-(--sys-color-disabled-text)'
          : isSelected
            ? cn(
                'cursor-pointer text-(--cmp-listItem-text-selected)',
                'bg-(--cmp-listItem-bg-selected)',
              )
            : isInteractive
              ? cn(
                  'cursor-pointer',
                  hasChevronInteraction
                    ? 'hover:bg-(--sys-color-bg-neutralBright)'
                    : 'hover:bg-(--cmp-listItem-bg-hover)',
                )
              : 'cursor-default',
        hasSupportingText && supportingTextPosition === 'bottom'
          ? dense
            ? 'min-h-[52px] py-(--cmp-listItem-paddingY-dense)'
            : 'min-h-[60px] py-(--cmp-listItem-paddingY-normal)'
          : dense
            ? 'h-[32px] py-(--cmp-listItem-paddingY-dense)'
            : 'h-[40px] py-(--cmp-listItem-paddingY-normal)',
        'gap-(--cmp-listItem-gap)',
        'pr-(--cmp-listItem-paddingX)',
        depth === 2 ? 'pl-[36px]' : depth === 3 ? 'pl-[56px]' : 'pl-[16px]',
        bordered
          ? cn(
              'overflow-hidden rounded-(--cmp-listItem-radius-bordered) border border-(--cmp-listItem-border-default)',
              isSelected
                ? 'border-(--cmp-listItem-bordered-border-selected)'
                : isInteractive && 'hover:border-(--cmp-listItem-bordered-border-hover)',
            )
          : 'border-b border-(--cmp-listItem-border-default)',
        className,
      )}
      {...rest}
    >
      {accentStripe && isSelected && !bordered && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-1px] left-0 top-0 w-(--cmp-listItem-accentStripe-width) bg-(--cmp-listItem-accentStripe-color)"
        />
      )}
      {leadingNode}
      <span
        className={cn(
          'min-w-0 flex-1',
          supportingTextPosition === 'bottom'
            ? 'flex flex-col'
            : 'flex items-center gap-[8px] whitespace-nowrap',
        )}
      >
        <span className="min-w-0 truncate">{titleNode}</span>
        {hasSupportingText && (
          <span
            className={cn(
              'typo-body-sm-default min-w-0 truncate text-(--sys-color-fg-secondary)',
              supportingTextPosition === 'right' && 'flex-1',
            )}
          >
            {supportingTextNode}
          </span>
        )}
      </span>
      {trailingNode}
      {showInteractionChevron && (
        <SIcon name="chevronRight" size={12} color={interactionChevronColor} />
      )}
    </div>
  );
});

export const SListItem = withDisplayName(SListItemImpl, 'SListItem');
